import * as React from 'react';
import type { ComparisonColumn, ComparisonItem } from '../types';
import { isEmptyColumn } from '../utils/helpers';

/**
 * Fires `onColumnView` once per column id following the same pattern edX uses:
 *
 *   const { ref, inView } = useInView(options);
 *   useEffect(() => { if (inView) elementViewed(); }, [inView]);
 *
 * The table extends this by also tracking which columns are in the carousel's
 * current visible window (`currentPage` ... `currentPage + visibleItems - 1`).
 *
 * Rules:
 * - `inView === false` → nothing fires (table not on screen yet)
 * - `inView` flips true → fires for every column currently in the window
 * - `inView` stays true + `currentPage` changes → fires for newly visible columns
 * - `columns` prop changes while other deps are unchanged → fires for any new
 *   column ids not yet in `firedRef` (handles item updates/reorders)
 * - Each column id fires at most once (tracked via `firedRef`)
 * - On mount `inView` is always false, so nothing fires until the user scrolls
 *
 * `firedRef` is passed in from the parent so desktop and mobile share the same
 * Set — both are mounted in the DOM simultaneously (one CSS-hidden) and would
 * otherwise each fire independently for the same column ids.
 */
export function useColumnViewObserver(
  columns: ComparisonColumn[],
  visibleItems: number,
  currentPage: number,
  inView: boolean,
  firedRef: React.RefObject<Set<string>>,
  onColumnView?: (column: ComparisonItem, index: number) => void,
): void {
  React.useEffect(() => {
    // Mirror of edX: if (inView) elementViewed()
    if (!inView) return;

    for (let i = currentPage; i < currentPage + visibleItems; i++) {
      const column = columns[i];
      if (!column || isEmptyColumn(column)) continue;
      if (firedRef.current?.has(column.id)) continue;
      firedRef.current?.add(column.id);
      onColumnView?.(column, i);
    }
    // `columns` and `onColumnView` are intentionally included so that if the
    // column list is updated while inView/currentPage/visibleItems are
    // unchanged, newly visible columns still fire.
  }, [inView, currentPage, visibleItems, firedRef, columns, onColumnView]);
}
